import imageCompression from "browser-image-compression";
import {
  deleteObject,
  getDownloadURL,
  ref as storageRef,
  uploadBytes,
} from "firebase/storage";
import { getFirebaseStorage } from "@/lib/firebase/client";

/**
 * Storage layout:
 *   households/{hid}/completions/{cid}/proof.jpg
 *
 * Plik nazywamy zawsze `proof.jpg` po kompresji do JPG — nadpisanie OK
 * (np. retake przed submitem). `cid` znamy dopiero po addDoc, więc:
 *   1. addDoc completion -> uzyskaj id
 *   2. uploadProof(hid, cid, file)
 *   3. updateDoc completion z proofPhotoUrl
 *
 * Alternatywa (przyjęta tutaj): uploadProofWithTempId — zapisujemy pod
 * tymczasowym crypto.randomUUID(), bo Storage rules nie wymagają, żeby
 * `cid` zgadzał się z istniejącym dokumentem (ścieżka jest przestrzenią
 * roboczą całego household). Po addDoc możemy reuploadować pod właściwy
 * cid jeśli chcemy spójności z firestore — ale dla MVP wystarczy temp UUID.
 */

const MAX_SIZE_MB = 0.15; // ~150KB target
const MAX_WIDTH = 1280;

export type CompressedPhoto = {
  blob: Blob;
  width: number;
  height: number;
  sizeBytes: number;
};

export async function compressForUpload(file: File): Promise<CompressedPhoto> {
  if (!file.type.startsWith("image/")) {
    throw new Error("Plik musi być zdjęciem");
  }
  const compressed = await imageCompression(file, {
    maxSizeMB: MAX_SIZE_MB,
    maxWidthOrHeight: MAX_WIDTH,
    // Worker biblioteki domyślnie importuje skrypt z CDN. W PWA na telefonie
    // to potrafi zablokować wybór zdjęcia i zostawić przycisk "Zgłoś" disabled.
    useWebWorker: false,
    fileType: "image/jpeg",
    initialQuality: 0.82,
  });
  // imageCompression zwraca File; rozmiar i wymiary łatwo wyciągnąć
  const dims = await readImageDims(compressed);
  return {
    blob: compressed,
    width: dims.w,
    height: dims.h,
    sizeBytes: compressed.size,
  };
}

function readImageDims(blob: Blob): Promise<{ w: number; h: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ w: img.naturalWidth, h: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Nie udało się odczytać zdjęcia"));
    };
    img.src = url;
  });
}

export async function uploadProofPhoto(
  hid: string,
  completionId: string,
  uid: string,
  blob: Blob,
): Promise<{ url: string; path: string }> {
  // UID w ścieżce — wymaga storage.rules: request.auth.uid == uid
  const path = `households/${hid}/completions/${completionId}/${uid}/proof.jpg`;
  const ref = storageRef(getFirebaseStorage(), path);
  await uploadBytes(ref, blob, {
    contentType: "image/jpeg",
    cacheControl: "public, max-age=31536000, immutable",
  });
  const url = await getDownloadURL(ref);
  return { url, path };
}

export async function deleteProofPhoto(path: string): Promise<void> {
  try {
    const ref = storageRef(getFirebaseStorage(), path);
    await deleteObject(ref);
  } catch {
    // Best-effort: jeśli nie ma — trudno
  }
}
